import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Truck } from 'lucide-react';
import {
  catalogueKeys,
  fetchProduct,
  fetchRelatedProducts,
  validatePersonalisation,
} from '@/api/catalogue';
import { PersonalisationForm } from '@/components/product/PersonalisationForm';
import { ColourwaySwatches } from '@/components/product/ColourwaySwatches';
import { COLOURWAYS, type ColourwayId } from '@/components/brand/BrandArtwork';
import { Hero3D } from '@/components/three/Hero3D';
import { PersonalisationPreview } from '@/components/product/PersonalisationPreview';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { ProductGallery } from '@/components/product/ProductGallery';
import { Product360Viewer } from '@/components/product/Product360Viewer';
import { ProductCard } from '@/components/product/ProductCard';
import { Price } from '@/components/product/Price';
import { Button } from '@/components/ui/Button';
import { QuantitySelector } from '@/components/ui/QuantitySelector';
import { Alert } from '@/components/ui/Alert';
import { ErrorState } from '@/components/system/ErrorState';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { usePlatform } from '@/providers/PlatformProvider';
import { useCart } from '@/providers/CartProvider';
import { ApiException } from '@/types/api';
import type {
  Money,
  PersonalisationOption,
  PersonalisationValues,
  ProductVariant,
} from '@/types/catalogue';

/**
 * Below this many frames a "spin" stutters badly enough to look broken, so
 * the modelled 3D box is the better experience. A real turntable sequence is
 * typically 24–36 frames.
 */
const MIN_SPIN_FRAMES = 8;

/**
 * Product detail — BUILD-PLAN §9, §39, §41, §62, §79.
 */
export function ProductDetailPage() {
  const { slug = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * Finish is read from the URL so a choice made on a product card carries
   * into this page, and so the page can be shared or bookmarked showing that
   * finish. An unrecognised value falls back rather than rendering nothing.
   */
  const finishParam = searchParams.get('finish');
  const colourway: ColourwayId | undefined = COLOURWAYS.some((c) => c.id === finishParam)
    ? (finishParam as ColourwayId)
    : undefined;

  function handleColourwayChange(next: ColourwayId) {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set('finish', next);

        return params;
      },
      // Replace, so cycling through finishes does not fill the back history.
      { replace: true },
    );
  }
  const { isVisible, isEnabled } = usePlatform();
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);

  const { add: addToCart } = useCart();
  const [view, setView] = useState<'gallery' | '3d'>('gallery');
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [personalisation, setPersonalisation] = useState<PersonalisationValues>({});
  const [personalisationErrors, setPersonalisationErrors] = useState<Record<string, string>>({});
  const [personalisationSurcharge, setPersonalisationSurcharge] = useState<Money | null>(null);

  /**
   * Preflight the entry against the server — §10.
   *
   * Debounced, because validating on every keystroke would issue a request
   * per character. The error for a field is cleared as soon as it is edited,
   * so a stale message never sits under a field the customer has since fixed.
   */
  const runValidation = useDebouncedCallback(async (next: PersonalisationValues) => {
    if (Object.keys(next).length === 0) {
      setPersonalisationSurcharge(null);

      return;
    }

    try {
      const result = await validatePersonalisation(slug, next);

      setPersonalisationErrors({});
      // The surcharge shown always comes from the server, never computed here.
      setPersonalisationSurcharge(result.surcharge);
    } catch (error) {
      if (error instanceof ApiException) {
        const mapped: Record<string, string> = {};

        for (const [field, message] of Object.entries(error.fieldErrors())) {
          mapped[field.replace(/^personalisation\./, '')] = message;
        }

        setPersonalisationErrors(mapped);
      }
    }
  }, 500);

  function handlePersonalisationChange(key: string, value: string) {
    const next = { ...personalisation, [key]: value };

    setPersonalisation(next);
    setPersonalisationErrors((current) => {
      const { [key]: _removed, ...rest } = current;

      return rest;
    });

    void runValidation(next);
  }


  const { data: product, isPending, isError, error, refetch } = useQuery({
    queryKey: catalogueKeys.product(slug),
    queryFn: () => fetchProduct(slug),
    enabled: slug !== '',
  });

  const { data: related = [] } = useQuery({
    queryKey: catalogueKeys.related(slug),
    queryFn: () => fetchRelatedProducts(slug),
    // Deferred until the product exists, so a 404 does not fire two requests.
    enabled: Boolean(product),
  });

  usePageMetadata({
    title: product?.seo.title ?? 'Loading',
    description: product?.seo.description ?? null,
    image: product?.images?.[0]?.url ?? null,
    type: 'product',
  });

  if (isPending) {
    return <ProductDetailSkeleton />;
  }

  // A missing product and an unpublished one both arrive as 404 by design
  // (§29), so both render the normal not-found page.
  if (isError && error instanceof ApiException && error.code === 'NOT_FOUND') {
    return <NotFoundPage />;
  }

  if (isError || !product) {
    return (
      <ErrorState
        title="We couldn’t load this piece"
        description="Something went wrong. Please try again in a moment."
        onRetry={() => void refetch()}
      />
    );
  }

  const personalisationOptions = product.personalisationOptions ?? [];

  /** Ribbon choice tints the preview, so the two selections visibly relate. */
  const ribbonHex = personalisationOptionsHex(personalisationOptions, personalisation);

  /*
   * Frames for a photographic 360 spin.
   *
   * Convention: images whose alt text is marked `spin` form the turntable
   * sequence, in `position` order. Using the existing images table means no
   * schema change and no separate upload path — a merchandiser uploads the
   * sequence the same way they upload any other product image.
   */
  const spinFrames = (product.images ?? [])
    .filter((image) => /\bspin\b/i.test(image.alt ?? ''))
    .map((image) => image.url);

  const variants = product.variants ?? [];
  const selectedVariant: ProductVariant | undefined =
    variants.find((variant) => variant.id === selectedVariantId) ??
    variants.find((variant) => variant.isDefault) ??
    variants[0];

  const displayPrice = selectedVariant?.price ?? product.price;

  /**
   * Adds the current selection to the cart.
   *
   * The payload states what is wanted — variant, quantity, personalisation —
   * and never what it costs. Pricing, stock and personalisation are all
   * re-decided server-side, so this is a request, not an instruction
   * (§13/§29).
   */
  async function handleAddToCart() {
    if (!selectedVariant) return;

    setAddError(null);
    setAddingToCart(true);

    try {
      await addToCart({
        variant_id: selectedVariant.id,
        quantity,
        ...(Object.keys(personalisation).length > 0 ? { personalisation } : {}),
      });
    } catch (error) {
      if (error instanceof ApiException) {
        const fields = error.fieldErrors();

        // Map any personalisation errors back onto their fields so the
        // customer sees them where they typed, not only as a banner.
        const mapped: Record<string, string> = {};
        for (const [field, message] of Object.entries(fields)) {
          if (field.startsWith('personalisation.')) {
            mapped[field.replace(/^personalisation\./, '')] = message;
          }
        }

        if (Object.keys(mapped).length > 0) {
          setPersonalisationErrors(mapped);
        }

        setAddError(Object.values(fields)[0] ?? error.message);
      } else {
        setAddError('We could not add this to your cart. Please try again.');
      }
    } finally {
      setAddingToCart(false);
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 sm:px-6 lg:px-8 lg:py-14">
      <Breadcrumbs product={product.name} category={product.categories?.[0]} />

      {/*
        Two columns from `md`, not `lg`.
        ----------------------------------------------------------------------
        Splitting only at `lg` gave the whole tablet band a single column, so
        the gallery ran the full 834px as a square — an 800px image with the
        price, options and Add to Basket pushed entirely below the fold. The
        page measured 5166px on tablet against 2807px on desktop: nearly twice
        as tall for the same content, purely because of one missing breakpoint.
      */}
      {/*
        Five columns of image to seven of information — not a 50/50 split.
        ----------------------------------------------------------------------
        An equal split gave the gallery half the page, which at 1440px is a
        604px square: the largest thing on the screen by a wide margin, with
        the price and the buy control pushed to the right edge of a viewport
        the eye reaches last. A shop is not a gallery. The product still needs
        to be seen, but the decision — size, finish, personalisation, total —
        is what the page is FOR, so it gets the greater share.

        (Splitting at `md` rather than `lg` is the other half of this: at `lg`
        the tablet band got one column and a full-width 790px square.)
      */}
      <div className="mt-6 grid gap-8 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:gap-10 lg:gap-14">
        {/* Sticky: the image stays with you while the options, personalisation
            and description scroll past it — otherwise you are choosing a
            finish with nothing to look at. */}
        <div className="flex flex-col gap-4 md:sticky md:top-24 md:self-start">
          {/*
            View switch — §62. The 3D box is the closest thing to picking the
            product up, so it sits beside the gallery rather than replacing
            it. Only rendered when there is something to switch to.
          */}
          <div className="flex items-center gap-2" role="tablist" aria-label="Product views">
            <ViewTab active={view === 'gallery'} onClick={() => setView('gallery')}>
              Gallery
            </ViewTab>
            <ViewTab active={view === '3d'} onClick={() => setView('3d')}>
              {spinFrames.length >= MIN_SPIN_FRAMES ? '360° Photo' : '3D View'}
            </ViewTab>
          </div>

          {view === 'gallery' ? (
            <ProductGallery
              images={product.images ?? []}
              productName={product.name}
              colourway={colourway}
            />
          ) : spinFrames.length >= MIN_SPIN_FRAMES ? (
            /*
             | A photographed turntable sequence is the real thing, so it wins
             | whenever one exists. Detected by frame count rather than a
             | manual flag: uploading the sequence is the only signal needed,
             | and nobody has to remember to tick a box.
             */
            <Product360Viewer frames={spinFrames} productName={product.name} />
          ) : (
            /*
             | No sequence uploaded — fall back to the modelled 3D box, which
             | turns freely without needing any photography. It is presented
             | as a rendering of the packaging, never as a photo of this
             | specific product (§86).
             */
            <div className="relative aspect-square w-full overflow-hidden bg-surface-muted shadow-card">
              <Hero3D seed={product.name} className="h-full w-full" />

              {/* The same inset keyline the product cards and occasion tiles
                  carry — the detail that makes an image read as mounted. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-3 border border-ivory/15"
              />
            </div>
          )}

          {/* Finish selector drives the artwork, the preview and the 3D box
              alike, so one choice is reflected everywhere on the page. */}
          <ColourwaySwatches
            value={colourway ?? defaultFinishFor(product.name)}
            onChange={handleColourwayChange}
          />
        </div>

        <div className="flex flex-col">
          {/* The same rule-and-eyebrow lockup the homepage sections and every
              page header use, so the product page belongs to the same system
              rather than starting its own. */}
          {product.categories?.[0] && (
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
              <p className="eyebrow !mt-0">{product.categories[0].name}</p>
            </div>
          )}

          <h1 className="mt-4 font-display text-[1.75rem] leading-[1.15] tracking-[-0.01em] text-ink sm:text-3xl lg:text-[2.25rem]">
            {product.name}
          </h1>

          <div className="mt-4 border-b border-line pb-6">
            <Price price={displayPrice} compareAt={product.compareAtPrice} size="lg" />
          </div>

          {product.shortDescription && (
            <p className="mt-6 max-w-md text-[0.9375rem] leading-[1.85] text-muted">
              {product.shortDescription}
            </p>
          )}

          {variants.length > 1 && (
            <fieldset className="mt-8">
              <legend className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-ink">
                Size
              </legend>

              <div className="flex flex-wrap gap-2">
                {variants.map((variant) => {
                  const isSelected = variant.id === selectedVariant?.id;

                  return (
                    <button
                      key={variant.id}
                      type="button"
                      // Out-of-stock options stay visible but unselectable —
                      // hiding them makes the range look smaller than it is.
                      disabled={!variant.inStock}
                      aria-pressed={isSelected}
                      onClick={() => setSelectedVariantId(variant.id)}
                      /* The same pill the filter panel uses for facets and
                         price brackets. Three different shapes for "choose
                         one of these" across a site is what makes it feel
                         assembled by different hands. */
                      className={`min-h-11 rounded-full border px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                        isSelected
                          ? 'border-accent bg-accent text-white'
                          : 'border-line bg-surface text-ink hover:border-accent hover:text-accent'
                      }`}
                    >
                      <span className="flex flex-col items-start leading-tight">
                        <span>{variant.name ?? 'Standard'}</span>
                        {/* Price shown per size, so the customer sees the cost
                            of the choice before making it (§86). */}
                        <span
                          className={`text-[0.6875rem] ${isSelected ? 'text-white/75' : 'text-muted'}`}
                        >
                          {variant.price.formatted}
                        </span>
                      </span>
                      {!variant.inStock && <span className="sr-only"> (out of stock)</span>}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          {/* Quantity — §9. Bounded by what is actually purchasable. */}
          <div className="mt-8 flex flex-wrap items-end gap-6">
            <div className="flex flex-col gap-3">
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-ink">
                Quantity
              </span>
              <QuantitySelector
                value={quantity}
                onChange={setQuantity}
                disabled={!product.isPurchasable}
              />
            </div>

            {/*
              Running total. Composed from server-provided figures only — the
              unit price and any personalisation surcharge both arrive as
              Money from the API, and quantity is a plain integer multiplier.
              No price arithmetic is invented on the client (§33).
            */}
            <div className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-[0.2em] text-muted">Total</span>
              <span className="font-display text-2xl text-ink">
                {formatTotal(displayPrice, personalisationSurcharge, quantity)}
              </span>
            </div>
          </div>

          {/*
            §10/§16 — only rendered when the product actually offers options,
            and only when the feature is live. A stale build cannot show the
            form for a product that no longer supports it.
          */}
          {isVisible('personalisation') && personalisationOptions.length > 0 && (
            <section className="mt-10 border-t border-line pt-8">
              <div className="flex items-center gap-3">
                <span className="h-px w-6 bg-accent/50" aria-hidden="true" />
                <h2 className="font-display text-xl text-ink">Make it personal</h2>
              </div>
              <p className="mt-1.5 text-sm text-muted">
                Tell us how you would like this piece finished.
              </p>

              {/* Stacks until `xl`. The information column is now 7/12 rather than a
              half, so putting a 15rem preview beside the fields from `lg`
              squeezed the inputs into a strip — the preview was competing
              with the thing it is meant to illustrate. */}
              <div className="mt-7 grid gap-8 xl:grid-cols-[1fr_minmax(0,14rem)]">
                <PersonalisationForm
                  options={personalisationOptions}
                  values={personalisation}
                  onChange={handlePersonalisationChange}
                  errors={personalisationErrors}
                />

                {/* Preview column — sticky on desktop so it stays visible
                    while the customer works down a long form. */}
                <div className="md:sticky md:top-24 md:self-start">
                  <PersonalisationPreview
                    productName={product.name}
                    values={personalisation}
                    {...(colourway ? { colourway } : {})}
                    {...(ribbonHex ? { ribbonHex } : {})}
                  />
                </div>
              </div>

              {personalisationSurcharge && (
                <p className="mt-6 text-sm text-muted">
                  Personalisation adds{' '}
                  <span className="text-ink">{personalisationSurcharge.formatted}</span> to this
                  piece.
                </p>
              )}
            </section>
          )}

          <div className="mt-8 flex flex-col gap-3">
            {selectedVariant?.isLowStock && (
              <Alert tone="info">Only a few left — this piece is in high demand.</Alert>
            )}

            {!product.inStock && (
              <Alert tone="info">
                This piece is currently out of stock. Please check back soon.
              </Alert>
            )}

            {/*
              §86 forbids a button that pretends to work. Cart is a later
              phase, so when it is not enabled the customer is told plainly
              rather than shown a control that does nothing.
            */}
            {isEnabled('cart') ? (
              <>
                <Button
                  size="lg"
                  fullWidth
                  loading={addingToCart}
                  disabled={!product.isPurchasable || !selectedVariant}
                  onClick={() => void handleAddToCart()}
                >
                  {product.isPurchasable ? 'Add to Cart' : 'Currently Unavailable'}
                </Button>

                {/*
                  Server-side rejections surface here — a required
                  personalisation field, or stock taken since the page loaded.
                  §41: the customer is told what to fix, not left guessing.
                */}
                {addError && <Alert tone="error">{addError}</Alert>}
              </>
            ) : (
              <Alert tone="info">
                Online ordering opens soon.{' '}
                <Link to="/contact" className="underline underline-offset-4">
                  Enquire about this piece
                </Link>
              </Alert>
            )}

            {product.hasPersonalisation && (
              <p className="inline-flex items-center gap-2 text-sm text-accent">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                This piece can be personalised
              </p>
            )}

            <p className="inline-flex items-center gap-2 text-sm text-muted">
              <Truck className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
              Beautifully packaged and dispatched with care
            </p>
          </div>

          {/* Even spacing between the stacked sections below: 10 / 12 / 10
              made the gaps look accidental rather than rhythmic. */}
          {product.description && (
            <section className="mt-10 border-t border-line pt-8">
              <div className="flex items-center gap-3">
                <span className="h-px w-6 bg-accent/50" aria-hidden="true" />
                <h2 className="font-display text-xl text-ink">Details</h2>
              </div>
              <div className="mt-4 whitespace-pre-line text-sm leading-relaxed text-muted">
                {product.description}
              </div>
            </section>
          )}

          {(product.materials || product.dimensions) && (
            <section className="mt-10 border-t border-line pt-8">
              <div className="flex items-center gap-3">
                <span className="h-px w-6 bg-accent/50" aria-hidden="true" />
                <h2 className="font-display text-xl text-ink">Specification</h2>
              </div>

              <dl className="mt-4 flex flex-col gap-3 text-sm">
                {product.materials && (
                  <div className="flex gap-4">
                    <dt className="w-28 shrink-0 text-muted">Materials</dt>
                    <dd className="text-ink">{product.materials}</dd>
                  </div>
                )}
                {product.dimensions && (
                  <div className="flex gap-4">
                    <dt className="w-28 shrink-0 text-muted">Dimensions</dt>
                    <dd className="text-ink">{product.dimensions}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}
        </div>
      </div>

      {/* mt-24 here was nearly a screen of empty space between the buy panel
          and the recommendations. */}
      {related.length > 0 && (
        <section className="mt-16 border-t border-line pt-12">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
            <p className="eyebrow !mt-0">More to consider</p>
          </div>

          <h2 className="mt-4 font-display text-[1.5rem] leading-tight text-ink sm:text-2xl">
            You may also like
          </h2>

          <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-9 sm:gap-x-6 lg:grid-cols-4">
            {related.slice(0, 4).map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Formats the line total for display.
 *
 * Works entirely in integer minor units taken from server-supplied Money
 * objects, then formats once at the end — the client never holds a decimal
 * price, so §33's no-floating-point rule holds on this side too.
 *
 * This is a *display* figure. The authoritative total is calculated by the
 * backend at add-to-cart and again at checkout; nothing here is trusted.
 */
function formatTotal(unit: Money, surcharge: Money | null, quantity: number): string {
  const perItemMinor = unit.minor + (surcharge?.minor ?? 0);
  const totalMinor = perItemMinor * quantity;

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: unit.currency,
    }).format(totalMinor / 100);
  } catch {
    // An unrecognised currency code must not blank the price.
    return `${unit.currency} ${(totalMinor / 100).toFixed(2)}`;
  }
}

/** Tab control for the gallery / 360 switch. */
function ViewTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`min-h-9 border-b-2 px-3 pb-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${
        active ? 'border-accent text-accent' : 'border-transparent text-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/** Stable per-product default finish, matching ProductCard's seeding. */
function defaultFinishFor(name: string): ColourwayId {
  let value = 5381;
  for (let i = 0; i < name.length; i++) {
    value = ((value << 5) + value + name.charCodeAt(i)) >>> 0;
  }

  return COLOURWAYS[value % COLOURWAYS.length]!.id;
}

/**
 * Resolves the hex of the chosen ribbon colour, if the product offers one.
 *
 * Looked up from the option's own configured choices rather than a local
 * colour map, so a colour added in admin flows straight through.
 */
function personalisationOptionsHex(
  options: PersonalisationOption[] | undefined,
  values: PersonalisationValues,
): string | undefined {
  const colourOption = options?.find((option) => option.type === 'colour');

  if (!colourOption) return undefined;

  const chosen = values[colourOption.key];

  return colourOption.config.choices?.find((choice) => choice.value === chosen)?.hex;
}

/** §39 breadcrumb trail. */
function Breadcrumbs({
  product,
  category,
}: {
  product: string;
  category?: { name: string; slug: string } | undefined;
}) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <li>
          <Link to="/" className="hover:text-accent">
            Home
          </Link>
        </li>
        <li aria-hidden="true">/</li>
        <li>
          <Link to="/shop" className="hover:text-accent">
            Shop
          </Link>
        </li>
        {category && (
          <>
            <li aria-hidden="true">/</li>
            <li>
              <Link
                to={`/shop?categories=${category.slug}`}
                className="hover:text-accent"
              >
                {category.name}
              </Link>
            </li>
          </>
        )}
        <li aria-hidden="true">/</li>
        <li aria-current="page" className="text-ink">
          {product}
        </li>
      </ol>
    </nav>
  );
}

/** Mirrors the real layout so nothing shifts when content arrives (§28). */
function ProductDetailSkeleton() {
  return (
    <div
      className="mx-auto max-w-7xl px-5 py-8 sm:px-6 lg:px-8 lg:py-14"
      role="status"
      aria-label="Loading product"
    >
      {/* Must match the real layout's breakpoints exactly, or the page
          reflows the moment the product arrives. */}
      <div className="grid gap-8 md:grid-cols-2 md:gap-10 lg:gap-16">
        <div className="aspect-square w-full animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />

        <div className="flex flex-col gap-4">
          <div className="h-9 w-3/4 animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />
          <div className="h-6 w-32 animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />
          <div className="mt-4 h-20 w-full animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />
          <div className="mt-6 h-12 w-full animate-pulse bg-ink/[0.09] motion-reduce:animate-none" />
        </div>
      </div>
    </div>
  );
}
